import * as React from 'react';

import { enumPrinterStatus } from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
  ThemeColor,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { printerDeleteAtom } from '../../../_graphql/mutationPrinterDelete';
import { printerUpdateAtom } from '../../../_graphql/mutationPrinterUpdate';
import { usePrinterEventsSubscription } from '../../../_graphql/subscriptionPrinterEvents';
import * as printerState from '../../../_state/printerDetail.state';
import {
  $discardPrinterBaseline,
  $hasUnsavedPrinterChanges,
  $resetPrinterFormToPrinter,
  PrinterFormState,
} from '../../../_state/printerForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulatePrinterForm } from '../../../hooks/usePopulatePrinterForm';
import { useTranslations } from '../../../i18n/useTranslations';
import type { PrinterStatus } from '../../../types/sidecar';

import { PrinterTabInfo } from './PrinterTabInfo';
import { PrinterTabQueue } from './PrinterTabQueue';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<PrinterStatus, ThemeColor> = {
  [enumPrinterStatus.Discovered]: 'warning',
};

type PrinterDetailTab = 'info' | 'queue';

export const PrinterDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] = React.useState<PrinterDetailTab>('info');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<PrinterDetailTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedPrinterChanges);
  const [, resetForm] = useAtom($resetPrinterFormToPrinter);
  const [discardBaseline, setDiscardBaseline] = useAtom(
    $discardPrinterBaseline,
  );

  const deletePrinter = useSetAtom(printerDeleteAtom({}));
  const updatePrinter = useSetAtom(printerUpdateAtom({}));

  const sidecarId = useAtomValue(printerState.$printerSidecarId);
  const printerId = useAtomValue(printerState.$printerId);

  const {
    data: printer,
    loadingState,
    error,
    refetch,
  } = useAtomValue(printerState.$remotePrinterDetail);

  const presenceMembers = useHardwarePresenceMembers();
  const { connection } = useHardwareSidecarConnection({
    sidecar: printer?.sidecarDetail,
  });

  usePopulatePrinterForm({ printer });

  usePrinterEventsSubscription(undefined, {
    onEvent: (event) => {
      if (event?.id === printerId) {
        refetch?.();
      }
    },
  });

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.hardwareView.printerDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: PrinterFormState) => {
      if (!printerId || !data) return;

      try {
        setIsUpdating(true);
        await updatePrinter({
          id: printerId,
          name: data.name.trim(),
          printerType: data.printerType,
          pageSize: data.pageSize,
          rollWidth: data.rollWidth,
        });

        setDiscardBaseline(data);
        toast.success(t.hardwareView.printerDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.printerDetailView.toast.updateError(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [printerId, updatePrinter, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (currentTab === 'info' && hasUnsavedChanges) {
        setPendingTabChange(newTab as PrinterDetailTab);
        return false;
      }
      return true;
    },
    [currentTab, hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!printerId) return;

    try {
      await deletePrinter({ id: printerId });
      toast.success(t.hardwareView.printerDetailView.messages.deleteSuccess);
      setLocation('/hardware');
    } catch (err) {
      toast.error(
        t.hardwareView.printerDetailView.messages.deleteError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
      throw err;
    }
  }, [printerId, deletePrinter, toast, setLocation, t]);

  if (loadingState.isInitialLoading && !printer) {
    return (
      <div className="@PrinterDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20" />
            <div className="h-8 w-1/3 rounded bg-neutral/20" />
            <div className="h-32 rounded bg-neutral/20" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !printer || !printerId || !sidecarId) {
    return (
      <div className="@PrinterDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.hardwareView.common.errorLabel}:{' '}
          {error?.message ??
            t.hardwareView.printerDetailView.messages.printerNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.printerDetailView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  const printerStatus = printer.status as PrinterStatus;
  const isOnline = presenceMembers.has(printer.id);

  return (
    <div className="@PrinterDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as PrinterDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={printer.name}
        headerActions={
          <ConfirmPopoverButton
            onConfirm={handleDelete}
            message={
              t.hardwareView.printerDetailView.messages.deleteConfirmation
            }
            confirmLabel={t.hardwareView.common.confirmDelete}
            cancelLabel={t.hardwareView.common.cancel}
            buttonProps={{
              variant: 'outline',
              color: 'failure',
              icon: <Trash2 className="size-4" />,
            }}
          >
            {t.hardwareView.printerDetailView.actions.delete}
          </ConfirmPopoverButton>
        }
        subtitle={
          <div className="flex items-center gap-2">
            <Badge color={STATUS_COLORS[printerStatus] ?? 'warning'}>
              {
                t.hardwareView.printersPanel.statusLabels[
                  printerStatus as keyof typeof t.hardwareView.printersPanel.statusLabels
                ]
              }
            </Badge>
            <Badge color={isOnline ? 'success' : 'neutral'}>
              {isOnline
                ? t.hardwareView.printerDetailView.presenceLabels.online
                : t.hardwareView.printerDetailView.presenceLabels.offline}
            </Badge>
            {connection?.connected && connection.type === 'direct' && (
              <Badge color="primary">
                {t.hardwareView.printerDetailView.connectionLabels.direct}
              </Badge>
            )}
            {connection?.connected && connection.type === 'relayed' && (
              <Badge color="neutral">
                {t.hardwareView.printerDetailView.connectionLabels.relayed}
              </Badge>
            )}
            {!connection?.connected && (
              <Badge color="warning">
                {t.hardwareView.printerDetailView.connectionLabels.disconnected}
              </Badge>
            )}
            <span className="text-neutral/60">{printer.driverType}</span>
            <span className="text-neutral/60">
              {'·'} {t.hardwareView.printerDetailView.fields.created}{' '}
              <LocalizedDate date={printer.timestamps.createdAt} />
            </span>
          </div>
        }
        tabs={{
          info: {
            title: t.hardwareView.printerDetailView.tabs.info,
            content: () => (
              <PrinterTabInfo
                printer={printer}
                printerId={printerId}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          queue: {
            title: t.hardwareView.printerDetailView.tabs.queue,
            content: () => (
              <PrinterTabQueue
                printer={printer}
                sidecarId={sidecarId}
                connection={connection}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.hardwareView.printerDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
